import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./backend-device-table.module.scss";
import { BackendDevice, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { useToggleable } from "../../../helper/hook-helper";
import VelavuIconButton from "../../../elements/velavu-icon-button";
import DeviceIcon from "../../../elements/device-icon";
import IconCircleAdd from "../../../dynamicicons/icon-circle-add";
import IconRemoveCircleFilled from "../../../dynamicicons/icon-remove-circle-filled";
import * as globals from "../../../styles/global.icss.scss";
import { classArr } from "../../../helper/style-helper";
import { VelavuScrollableRoundTable } from "../../../elements/velavu-scrollable-round-table";

export interface BackendDeviceTableProps {
	className?: string;
	devices?: BackendDevice[];
	configureDevice?: (deviceID: string) => void;
	removeDevice?: (deviceID: string) => void;
	disabled?: boolean;
}

interface TableDevice {
	id: string;
	model: string | undefined;
}

interface ConfiguredTableDevice extends TableDevice {
	connected: boolean;
}

export default function BackendDeviceTable(props: BackendDeviceTableProps) {
	const { t } = useTranslation();

	//Fetch devices
	const [devices, setDevices] = useState<VelavuDevice[] | undefined>();
	useEffect(() => {
		VelavuAPI.devices
			.getAllDevices({
				hardware: ["argo"],
			})
			.then((result) => {
				//TODO: Paginate devices
				setDevices(result.data);
			})
			.catch((error) => {
				console.warn("Failed to fetch devices", error);
			});
	}, [setDevices]);

	//Split devices by configured / available
	const [configuredDevices, availableDevices] = useMemo(() => {
		const backendDevices = props.devices ?? [];

		const configuredDevices: ConfiguredTableDevice[] = [];
		const availableDevices: TableDevice[] = [];

		if (devices !== undefined) {
			for (const device of devices) {
				const backendDevice = backendDevices.find(
					(backendDevice) => backendDevice.id === device.id,
				);
				if (backendDevice !== undefined) {
					configuredDevices.push({
						id: device.id,
						model: device.model,
						connected: backendDevice.connected,
					});
				} else {
					availableDevices.push({
						id: device.id,
						model: device.model,
					});
				}
			}
		}

		return [configuredDevices, availableDevices];
	}, [props.devices, devices]);

	return (
		<div
			className={classArr(
				props.className,
				styles.twoColumn,
				props.disabled && styles.disabled,
			)}
		>
			<div>
				<span className={styles.title}>
					{t("settings.broker.configured_devices")}
				</span>

				<VelavuScrollableRoundTable
					className={styles.table}
					layout={[
						{ flex: 2 },
						{ flex: 3 },
						{ width: 30, alignment: "end" },
					]}
				>
					<thead>
						<tr>
							<th>{t("elements.device_select.device_id")}</th>
							<th>{t("common.status")}</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{configuredDevices.map((device) => (
							<ConfiguredDeviceRow
								key={device.id}
								device={device}
								onMove={() => props.removeDevice?.(device.id)}
								disabled={props.disabled}
							/>
						))}
					</tbody>
				</VelavuScrollableRoundTable>
			</div>

			<div>
				<span className={styles.title}>
					{t("settings.broker.available_devices")}
				</span>

				<VelavuScrollableRoundTable
					className={styles.table}
					layout={[{ flex: 2 }, { width: 30, alignment: "end" }]}
				>
					<thead>
						<tr>
							<th>{t("elements.device_select.device_id")}</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{availableDevices.map((device) => (
							<AvailableDeviceRow
								key={device.id}
								device={device}
								onMove={() =>
									props.configureDevice?.(device.id)
								}
								disabled={props.disabled}
							/>
						))}
					</tbody>
				</VelavuScrollableRoundTable>
			</div>
		</div>
	);
}

function ConfiguredDeviceRow(props: {
	device: ConfiguredTableDevice;
	onMove: () => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const [hoverState, enterHoverState, exitHoverState] = useToggleable();

	return (
		<tr
			className={styles.tableRow}
			onMouseEnter={enterHoverState}
			onMouseLeave={exitHoverState}
		>
			<td>
				<DeviceIcon
					className={styles.rowIcon}
					model={props.device.model}
				/>
				<span style={{ marginLeft: 8 }}>{props.device.id}</span>
			</td>
			<td>
				{props.device.connected ? (
					<span className={styles.active}>
						{t("settings.broker.connected")}
					</span>
				) : (
					<span className={styles.inactive}>
						{t("settings.broker.disconnected")}
					</span>
				)}
			</td>
			<td>
				{!props.disabled && (
					<VelavuIconButton
						className={classArr(
							styles.tableButton,
							hoverState && styles.tableButtonHover,
						)}
						onClick={props.onMove}
					>
						<IconRemoveCircleFilled
							size={16}
							color={globals.textPrimary}
						/>
					</VelavuIconButton>
				)}
			</td>
		</tr>
	);
}

function AvailableDeviceRow(props: {
	device: TableDevice;
	onMove: () => void;
	disabled?: boolean;
}) {
	const [hoverState, enterHoverState, exitHoverState] = useToggleable();

	return (
		<tr
			className={styles.tableRow}
			onMouseEnter={enterHoverState}
			onMouseLeave={exitHoverState}
		>
			<td>
				<DeviceIcon
					className={styles.rowIcon}
					model={props.device.model}
				/>
				<span style={{ marginLeft: 8 }}>{props.device.id}</span>
			</td>
			<td>
				{!props.disabled && (
					<VelavuIconButton
						className={classArr(
							styles.tableButton,
							hoverState && styles.tableButtonHover,
						)}
						onClick={props.onMove}
					>
						<IconCircleAdd size={16} color={globals.textPrimary} />
					</VelavuIconButton>
				)}
			</td>
		</tr>
	);
}
